// project imports
// action - state management
import {
  CUMULATIVE_MEMBERS_REDUCER_KEY,
  NEW_INSTALLS_REDUCER_KEY,
  SUBSCRIBER_ACQUISITION_ASSUMPTIONS_REDUCER_KEY,
} from "..";
import * as actionTypes from "../../actions/actionTypes";
import {
  COST_PER_INSTALL_KEY,
  MEDIA_BUYING_KEY,
} from "../assumptions/subscriberAcquisitionAssumptionsReducer";
import { itemType } from "../types";
import { years } from "store/utils/years";
import { TOTAL_KEY } from "./cumulativeMembersReducer";
import {
  installMembersMonths,
  newInstallsMonths,
  organicMonths,
  organicPercentageMonths,
  paidInstallsMonths,
  paidMonths,
  paidPercentageMonths,
} from "./staticYearsData";

// This is to filter based on company types
import { SaaSCoType, GeneralCoType } from "constants/companyTypes";

// subscriber acquisition assumption' keys
export const NEW_INSTALLS_KEY = "newInstalls";
export const ORGANIC_KEY = "organic";
export const ORGANIC_INSTALL_TO_MEMBERS_KEY = "installMembers";
export const ORGANIC_INSTALL_TO_PAID_INSTALLS_KEY = "paidInstalls";
export const PAID_KEY = "paid";
export const ORGANIC_PERCENTAGE_KEY = "organicPercentage";
export const PAID_PERCENTAGE_KEY = "paidPercentage";

export const initialState = {
  [NEW_INSTALLS_KEY]: {
    title: "New Installs",
    unit: "NUMBER",
    unitSymbol: "#",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      const result =
        Number(
          getReducerFunc(NEW_INSTALLS_REDUCER_KEY)[ORGANIC_KEY].calculate(
            yearKey,
            getReducerFunc
          )
        ) +
        Number(
          getReducerFunc(NEW_INSTALLS_REDUCER_KEY)[PAID_KEY].calculate(
            yearKey,
            getReducerFunc
          )
        );
      return result.toFixed(0);
    },
    months: newInstallsMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [ORGANIC_KEY]: {
    title: "Organic",
    unit: "NUMBER",
    unitSymbol: "#",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      // FIXME
      let cumulativeMembersTotal = getReducerFunc(
        CUMULATIVE_MEMBERS_REDUCER_KEY
      )[TOTAL_KEY]["initialPeriod"];

      if (yearKey > 1) {
        cumulativeMembersTotal = getReducerFunc(CUMULATIVE_MEMBERS_REDUCER_KEY)[
          TOTAL_KEY
        ].calculate(yearKey - 1, getReducerFunc);
      }

      const organicInstallToMembers = getReducerFunc(NEW_INSTALLS_REDUCER_KEY)[
        ORGANIC_INSTALL_TO_MEMBERS_KEY
      ]["years"][yearKey];

      const organicInstallToPaidInstalls = getReducerFunc(
        NEW_INSTALLS_REDUCER_KEY
      )[ORGANIC_INSTALL_TO_PAID_INSTALLS_KEY]["years"][yearKey];

      const paid = getReducerFunc(NEW_INSTALLS_REDUCER_KEY)[PAID_KEY].calculate(
        yearKey,
        getReducerFunc
      );

      const result =
        Number((cumulativeMembersTotal * organicInstallToMembers) / 100) +
        Number((organicInstallToPaidInstalls * paid) / 100);

      return result.toFixed(0);
    },
    months: organicMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [ORGANIC_INSTALL_TO_MEMBERS_KEY]: {
    title: "Organic (Install to Members)",
    unit: "PERCENTAGE",
    unitSymbol: "%",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.INPUT,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return 0;
    },
    months: installMembersMonths,
    years: {
      [years.YEAR_1_KEY]: 0.5,
      [years.YEAR_2_KEY]: 0.5,
      [years.YEAR_3_KEY]: 0.5,
      [years.YEAR_4_KEY]: 0.5,
      [years.YEAR_5_KEY]: 0.5,
    },
  },
  [ORGANIC_INSTALL_TO_PAID_INSTALLS_KEY]: {
    title: "Organic (Install to Paid Installs)",
    unit: "PERCENTAGE",
    unitSymbol: "%",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.INPUT,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return 0;
    },
    months: paidInstallsMonths,
    years: {
      [years.YEAR_1_KEY]: 35,
      [years.YEAR_2_KEY]: 35,
      [years.YEAR_3_KEY]: 35,
      [years.YEAR_4_KEY]: 37,
      [years.YEAR_5_KEY]: 37,
    },
  },
  [PAID_KEY]: {
    title: "Paid",
    unit: "NUMBER",
    unitSymbol: "#",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return (
        Number(
          getReducerFunc(SUBSCRIBER_ACQUISITION_ASSUMPTIONS_REDUCER_KEY)[
            MEDIA_BUYING_KEY
          ]["years"][yearKey]
        ) /
        Number(
          getReducerFunc(SUBSCRIBER_ACQUISITION_ASSUMPTIONS_REDUCER_KEY)[
            COST_PER_INSTALL_KEY
          ]["years"][yearKey]
        )
      );
    },
    months: paidMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [ORGANIC_PERCENTAGE_KEY]: {
    title: "Organic",
    unit: "PERCENTAGE",
    unitSymbol: "%",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      const result =
        Number(
          getReducerFunc(NEW_INSTALLS_REDUCER_KEY)[ORGANIC_KEY].calculate(
            yearKey,
            getReducerFunc
          )
        ) /
        Number(
          getReducerFunc(NEW_INSTALLS_REDUCER_KEY)[NEW_INSTALLS_KEY].calculate(
            yearKey,
            getReducerFunc
          )
        );
      return Number.isNaN(result) || !Number.isFinite(result)
        ? 0
        : result * 100;
    },
    months: organicPercentageMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [PAID_PERCENTAGE_KEY]: {
    title: "Paid",
    unit: "PERCENTAGE",
    unitSymbol: "%",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      const result =
        Number(
          getReducerFunc(NEW_INSTALLS_REDUCER_KEY)[PAID_KEY].calculate(
            yearKey,
            getReducerFunc
          )
        ) /
        Number(
          getReducerFunc(NEW_INSTALLS_REDUCER_KEY)[NEW_INSTALLS_KEY].calculate(
            yearKey,
            getReducerFunc
          )
        );
      return Number.isNaN(result) || !Number.isFinite(result)
        ? 0
        : result * 100;
    },
    months: paidPercentageMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
};

// ==============================|| SUBSCRIPTION ACQUISITION ASSUMPTIONS REDUCER ||============================== //

const newInstallsReducer = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.SET_NEW_INSTALL_BY_YEAR:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          years: {
            ...state[action.key].years,
            [action.yearKey]: [action.amount],
          },
        },
      };
    case actionTypes.SET_NEW_INSTALL_BY_MONTH:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          months: {
            ...state[action.key].months,
            [action.yearKey]: [action.amount],
          },
        },
      };
    case actionTypes.REPLACE_NEW_INSTALL_YEARS:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          months: action?.payload,
        },
      };
    case actionTypes.RESET_NEW_INSTALL_REDUCER:
      return initialState;

    default:
      return state;
  }
};

export default newInstallsReducer;
